import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { projects as projectsApi } from "@/lib/api";
import { DeckActionsModal } from "@/components/DeckActionsModal";
import { toast } from "sonner";
import { Loader2, Download, Plus, Sparkles, Share2, Settings2 } from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [list, setList] = useState(null);
  const [activeDeck, setActiveDeck] = useState(null);

  const reload = () => projectsApi.list().then(setList).catch(() => {});

  useEffect(() => {
    if (ready && !user) nav("/auth");
    if (user) reload();
    // eslint-disable-next-line
  }, [user, ready, nav]);

  if (!user) return null;

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="pt-10 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="kd-tag mb-3">Dashboard</div>
              <h1 className="kd-display text-4xl md:text-5xl tracking-tight">
                Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}.
              </h1>
              <p className="text-zinc-400 mt-2">Your saved decks and downloads live here.</p>
            </div>
            <button onClick={() => nav("/create")} data-testid="dashboard-new" className="rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white font-medium transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)] flex items-center gap-2">
              <Plus size={16} /> New deck
            </button>
          </div>

          {!list ? (
            <div className="flex items-center gap-2 text-zinc-400"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : list.length === 0 ? (
            <div className="kd-panel p-12 text-center">
              <Sparkles size={28} className="mx-auto mb-4 text-[#FF2A2D]" />
              <h2 className="kd-display text-2xl mb-2">No decks yet.</h2>
              <p className="text-zinc-400 text-sm mb-6">Build your first cinematic pitch deck in a few clicks.</p>
              <button onClick={() => nav("/create")} data-testid="empty-cta" className="rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)]">
                Generate my first deck
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`deck-card-${i}`}
                  className="rounded-3xl p-6 bg-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-colors"
                >
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden mb-4 bg-black relative">
                    {p.main_image ? (
                      <img src={p.main_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">No cover</div>
                    )}
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">{p.creator_type}</div>
                      <div className="kd-display text-xl text-white leading-tight mt-1">{p.info?.stage_name || "Untitled"}</div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                  </div>
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-3">
                    <span>{p.deck_size} pages · {p.status}</span>
                    <span>{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => nav(`/create/${p.id}`)} data-testid={`deck-edit-${i}`} className="flex-1 rounded-full py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">Open</button>
                    <button onClick={() => setActiveDeck(p)} data-testid={`deck-actions-${i}`} className="rounded-full px-3 py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 transition-colors" title="Share, revise, graphics">
                      <Settings2 size={12} />
                    </button>
                    {p.share_token && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/deck/${p.share_token}`);
                          toast.success("Copied share link");
                        }}
                        data-testid={`deck-share-copy-${i}`}
                        className="rounded-full px-3 py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                        title="Copy public link"
                      >
                        <Share2 size={12} />
                      </button>
                    )}
                    {p.pdf_path && (
                      <a href={projectsApi.pdfUrl(p.id)} target="_blank" rel="noreferrer" data-testid={`deck-download-${i}`} className="rounded-full px-4 py-2 text-xs bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-colors flex items-center gap-1.5">
                        <Download size={12} /> PDF
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      {activeDeck && (
        <DeckActionsModal
          deck={activeDeck}
          onClose={() => setActiveDeck(null)}
          onUpdate={reload}
        />
      )}
    </div>
  );
}
